import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ArtistBrowseSection({
  filteredArtists,
  isArtistsLoading,
  artistSearch,
  artistGenreFilter,
  onArtistSearchChange,
  onGenreFilterChange,
  onOpenArtistContact,
  onViewArtistProfile,
}) {
  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
      <section className="panel p-6">
        <div className="flex flex-col gap-4 border-b border-zinc-100 pb-5 dark:border-white/[0.06] sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              Discover artists
            </h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Browse artists by genre and reach out directly to plan your next event.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <div className="w-full sm:max-w-xs">
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="artist-search"
              >
                Search
              </label>
              <Input
                id="artist-search"
                value={artistSearch}
                onChange={(e) => onArtistSearchChange(e.target.value)}
                placeholder="Name, instrument, keyword"
              />
            </div>
            <div className="w-full sm:max-w-xs">
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="artist-genre"
              >
                Genre
              </label>
              <Input
                id="artist-genre"
                value={artistGenreFilter}
                onChange={(e) => onGenreFilterChange(e.target.value)}
                placeholder="e.g. Jazz"
              />
            </div>
          </div>
        </div>

        <div className="mt-5">
          {isArtistsLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={`artist-skeleton-${i}`} className="rounded-xl border border-zinc-100 dark:border-white/[0.06] p-5">
                  <div className="flex animate-pulse flex-col gap-3">
                    <div className="h-28 w-full rounded-lg bg-zinc-200 dark:bg-white/[0.08]" />
                    <div className="h-4 w-2/3 rounded-md bg-zinc-200 dark:bg-white/[0.08]" />
                    <div className="h-3 w-full rounded-md bg-zinc-100 dark:bg-white/[0.05]" />
                    <div className="h-3 w-5/6 rounded-md bg-zinc-100 dark:bg-white/[0.05]" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredArtists.length === 0 ? (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              No artists match your filters right now.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filteredArtists.map((artist) => {
                const hasCover = Boolean(artist.cover_image_url);
                const coverStyle = hasCover
                  ? {
                      backgroundImage: `linear-gradient(160deg, rgba(9,9,11,0.65), rgba(9,9,11,0.88)), url(${artist.cover_image_url})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }
                  : undefined;

                return (
                  <div
                    key={artist.id}
                    className={`flex h-full flex-col rounded-xl border p-5 transition-shadow hover:shadow-md ${
                      hasCover
                        ? "border-transparent text-white"
                        : "border-zinc-200/60 bg-white/60 dark:border-white/[0.07] dark:bg-white/[0.03]"
                    }`}
                    style={coverStyle}
                  >
                    <div className="flex flex-col gap-2">
                      <div>
                        <p className={`text-[11px] font-semibold uppercase tracking-[0.1em] ${hasCover ? "text-white/60" : "text-zinc-400 dark:text-zinc-500"}`}>
                          Artist
                        </p>
                        <h3 className={`text-lg font-bold tracking-tight ${hasCover ? "text-white" : "text-zinc-900 dark:text-zinc-50"}`}>
                          {artist.name}
                        </h3>
                        <p className={`text-xs ${hasCover ? "text-white/60" : "text-zinc-400 dark:text-zinc-500"}`}>
                          @{artist.username}
                        </p>
                      </div>
                      {artist.bio && (
                        <p className={`text-sm leading-relaxed ${hasCover ? "text-white/80" : "text-zinc-600 dark:text-zinc-300"}`}>
                          {artist.bio}
                        </p>
                      )}
                      {(artist.genres || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {(artist.genres || []).map((genre) => (
                            <span
                              key={`${artist.id}-genre-${genre}`}
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                                hasCover
                                  ? "bg-white/15 text-white"
                                  : "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                              }`}
                            >
                              {genre}
                            </span>
                          ))}
                        </div>
                      )}
                      {(artist.instruments || []).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {(artist.instruments || []).map((instrument) => (
                            <span
                              key={`${artist.id}-instrument-${instrument}`}
                              className={`text-xs ${hasCover ? "text-white/60" : "text-zinc-500 dark:text-zinc-400"}`}
                            >
                              {instrument}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className={`mt-3 flex flex-wrap gap-3 border-t pt-3 text-xs ${hasCover ? "border-white/15" : "border-zinc-100 dark:border-white/[0.06]"}`}>
                      {artist.experience_level && (
                        <span className={hasCover ? "text-white/70" : "text-zinc-500 dark:text-zinc-400"}>
                          {artist.experience_level}
                        </span>
                      )}
                      {artist.website && (
                        <a
                          className={`underline underline-offset-2 ${hasCover ? "text-amber-300 hover:text-amber-200" : "text-amber-600 hover:text-amber-700 dark:text-amber-400"}`}
                          href={artist.website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Website
                        </a>
                      )}
                    </div>

                    <div className="mt-auto pt-4 flex flex-col gap-2 sm:flex-row">
                      <Button
                        type="button"
                        className="btn-secondary w-full sm:w-auto text-sm"
                        onClick={() => onViewArtistProfile(artist)}
                      >
                        View profile
                      </Button>
                      <Button
                        type="button"
                        className="btn-primary w-full sm:w-auto text-sm"
                        onClick={() => onOpenArtistContact(artist)}
                      >
                        Contact
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
